import ArticleFormModal from '@/components/orders/ArticleFormModal'
import ServicePill from '@/components/orders/ServicePill'
import { useRole } from '@/hooks/useRole'
import { Article, ArticleService, Order, Service } from '@/types'
import { router } from '@inertiajs/react'
import { ActionIcon, Button, Group, Modal, NumberInput, Paper, Select, Stack, Text, TextInput } from '@mantine/core'
import { IconCopy, IconPencil, IconPlus, IconTrash } from '@tabler/icons-react'
import { FocusEvent, useEffect, useState } from 'react'

export default function ArticleCard({
  item,
  articleServices,
  organizationSlug,
  orderId,
  isCompanyCustomer,
  existingReferences,
  isLocked,
  canDelete,
}: {
  item: Article
  articleServices: ArticleService[]
  organizationSlug: string
  orderId: number
  isCompanyCustomer: boolean
  existingReferences: string[]
  isLocked: boolean
  canDelete: boolean
}) {
  const [showServiceForm, setShowServiceForm] = useState(false)
  const [editingService, setEditingService] = useState<Service | null>(null)
  const [selectedService, setSelectedService] = useState<string | null>(null)
  const [instructions, setInstructions] = useState('')
  const [price, setPrice] = useState<number | string>('')

  const { isBoutique } = useRole()
  // Editing an article is a delete-then-recreate, so it requires delete rights on a launched order.
  // In-place edits (reference, services) intentionally stay open: only deletion is restricted.
  const canEditArticle = !isLocked && canDelete
  const [showEditModal, setShowEditModal] = useState(false)
  const [reference, setReference] = useState(item.reference)

  useEffect(() => {
    setReference(item.reference)
  }, [item.reference])

  const isPlaceholder = item.services.length === 0
  const [showSetupModal, setShowSetupModal] = useState(false)

  const handleOpenEditModal = () => setShowEditModal(true)
  const handleOpenSetupModal = () => setShowSetupModal(true)
  const handleOpenServiceForm = () => setShowServiceForm(true)

  const handleUpdateReference = (value: string) => {
    setReference(value)
    router.patch(
      `/organizations/${organizationSlug}/orders/${orderId}/articles/${item.id}`,
      { article: { reference: value } },
      { preserveScroll: true }
    )
  }

  const handleEditArticle = (data: {
    articleName: string
    serviceName: string
    price: number
    instructions: string
    reference: string
  }) => {
    router.delete(`/organizations/${organizationSlug}/orders/${orderId}/articles/${item.id}`, {
      preserveScroll: true,
      onSuccess: () => {
        router.post(
          `/organizations/${organizationSlug}/orders/${orderId}/articles`,
          { article: { article_name: data.articleName, reference: data.reference } },
          {
            preserveScroll: true,
            onSuccess: (page) => {
              const orders = page.props.orders as Order[] | undefined
              const updatedOrder = orders ? orders.find((o) => o.id === orderId) : (page.props.order as Order | undefined)
              const newArticle = updatedOrder
                ? [...updatedOrder.articles]
                    .reverse()
                    .find((a) => a.article_name === data.articleName && a.services.length === 0)
                : null

              if (newArticle?.id) {
                router.post(
                  `/organizations/${organizationSlug}/orders/${orderId}/articles/${newArticle.id}/services`,
                  {
                    service: {
                      service_name: data.serviceName,
                      instructions: data.instructions,
                      price: data.price,
                    },
                  },
                  { preserveScroll: true, onSuccess: () => setShowEditModal(false) }
                )
              } else {
                setShowEditModal(false)
              }
            },
          }
        )
      },
    })
  }

  const handleRemove = () =>
    router.delete(`/organizations/${organizationSlug}/orders/${orderId}/articles/${item.id}`, {
      preserveScroll: true,
    })

  const handleReferenceBlur = (event: FocusEvent<HTMLInputElement>) => handleUpdateReference(event.currentTarget.value)

  const handleDuplicateArticle = () =>
    router.post(
      `/organizations/${organizationSlug}/orders/${orderId}/articles`,
      {
        article: {
          article_name: item.article_name,
          reference: item.reference,
          services_attributes: item.services.map((service) => ({
            service_name: service.service_name,
            price: service.price,
            instructions: service.instructions || '',
          })),
        },
      },
      { preserveScroll: true }
    )

  const handleSetupSubmit = (data: {
    articleName: string
    serviceName: string
    price: number
    instructions: string
    reference: string
  }) => {
    router.patch(
      `/organizations/${organizationSlug}/orders/${orderId}/articles/${item.id}`,
      { article: { article_name: data.articleName, reference: data.reference } },
      {
        preserveScroll: true,
        onSuccess: () => {
          router.post(
            `/organizations/${organizationSlug}/orders/${orderId}/articles/${item.id}/services`,
            {
              service: {
                service_name: data.serviceName,
                instructions: data.instructions,
                price: data.price,
              },
            },
            { preserveScroll: true, onSuccess: () => setShowSetupModal(false) }
          )
        },
      }
    )
  }

  const filteredServices = (articleServices || []).filter((s) => s.article_name === item.article_name)

  const currentService = filteredServices.find((s) => s.service_name === selectedService)

  useEffect(() => {
    if (currentService) {
      setPrice(currentService.price)
    } else {
      setPrice('')
    }
  }, [currentService])

  const handleEditService = (service: Service) => {
    setEditingService(service)
    setSelectedService(service.service_name)
    setPrice(service.price)
    setInstructions(service.instructions || '')
    setShowServiceForm(true)
  }

  const resetServiceForm = () => {
    setShowServiceForm(false)
    setEditingService(null)
    setSelectedService(null)
    setInstructions('')
    setPrice('')
  }

  const isMissingPrice = price === '' || price === null || price === undefined

  const handleSubmitService = () => {
    if (!selectedService || isMissingPrice) return

    const serviceData = {
      service: {
        service_name: selectedService,
        instructions,
        price: typeof price === 'string' ? parseFloat(price) : price,
      },
    }

    if (editingService) {
      router.patch(
        `/organizations/${organizationSlug}/orders/${orderId}/articles/${item.id}/services/${editingService.id}`,
        serviceData,
        { preserveScroll: true, onSuccess: resetServiceForm }
      )
    } else {
      router.post(`/organizations/${organizationSlug}/orders/${orderId}/articles/${item.id}/services`, serviceData, {
        preserveScroll: true,
        onSuccess: resetServiceForm,
      })
    }
  }

  const handleRemoveService = (serviceId: number) => {
    router.delete(`/organizations/${organizationSlug}/orders/${orderId}/articles/${item.id}/services/${serviceId}`, {
      preserveScroll: true,
    })
  }

  const [orderedServices, setOrderedServices] = useState(item.services)

  useEffect(() => {
    setOrderedServices(item.services)
  }, [item.services])

  const handleMoveUp = (id: number) => {
    const index = orderedServices.findIndex((s) => s.id === id)
    if (index > 0) {
      const newServices = [...orderedServices]
      ;[newServices[index - 1], newServices[index]] = [newServices[index], newServices[index - 1]]
      setOrderedServices(newServices)
    }
  }

  const handleMoveDown = (id: number) => {
    const index = orderedServices.findIndex((s) => s.id === id)
    if (index < orderedServices.length - 1) {
      const newServices = [...orderedServices]
      ;[newServices[index + 1], newServices[index]] = [newServices[index], newServices[index + 1]]
      setOrderedServices(newServices)
    }
  }

  if (isPlaceholder) {
    return (
      <>
        <Paper
          p="md"
          withBorder
          style={isLocked ? undefined : { cursor: 'pointer' }}
          onClick={isLocked ? undefined : handleOpenSetupModal}
        >
          <Group justify="space-between" align="center">
            <Group gap="xs">
              <IconPlus size={16} color="var(--mantine-color-brand-6)" />
              <Text c="brand" fw={500}>
                {item.article_name}
              </Text>
            </Group>
            {canDelete && (
              <ActionIcon
                variant="subtle"
                color="red"
                title="Supprimer l'article"
                onClick={(e) => {
                  e.stopPropagation()
                  handleRemove()
                }}
                size="lg"
              >
                <IconTrash size={18} />
              </ActionIcon>
            )}
          </Group>
        </Paper>

        <ArticleFormModal
          opened={showSetupModal}
          onClose={() => setShowSetupModal(false)}
          onSubmit={handleSetupSubmit}
          articleServices={articleServices}
          isCompanyCustomer={isCompanyCustomer}
          existingReferences={existingReferences}
          title="Définir l'article"
          submitLabel="Confirmer"
        />
      </>
    )
  }

  return (
    <Paper p="md" withBorder>
      <Stack gap="xs">
        <Group justify="space-between" align="center">
          <Group gap={4}>
            <Text
              fw={600}
              style={canEditArticle ? { cursor: 'pointer' } : undefined}
              onClick={canEditArticle ? handleOpenEditModal : undefined}
            >
              {item.article_name}
            </Text>
            {canEditArticle && (
              <ActionIcon variant="subtle" color="gray" onClick={handleOpenEditModal}>
                <IconPencil size={14} />
              </ActionIcon>
            )}
          </Group>

          <Group>
            {!isLocked && (
              <>
                <Button variant="light" size="xs" leftSection={<IconPlus size={16} />} onClick={handleOpenServiceForm}>
                  Ajouter un service
                </Button>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  title="Dupliquer l'article"
                  onClick={handleDuplicateArticle}
                  size="lg"
                >
                  <IconCopy size={18} />
                </ActionIcon>
              </>
            )}
            {canDelete && (
              <ActionIcon variant="subtle" color="red" title="Supprimer l'article" onClick={handleRemove} size="lg">
                <IconTrash size={18} />
              </ActionIcon>
            )}
          </Group>
        </Group>

        {orderedServices.length > 0 && (
          <Stack gap={6} pl={4} align="flex-start">
            {orderedServices.map((service, index) => (
              <ServicePill
                key={service.id}
                service={service}
                onRemove={handleRemoveService}
                onEdit={handleEditService}
                onMoveUp={index > 0 ? handleMoveUp : undefined}
                onMoveDown={index < orderedServices.length - 1 ? handleMoveDown : undefined}
                readOnly={isLocked}
                canRemove={canDelete}
              />
            ))}
          </Stack>
        )}
        {isCompanyCustomer && (
          <TextInput
            label="Référence"
            placeholder="Numero de commande ou nom du client"
            value={reference}
            onChange={(e) => setReference(e.currentTarget.value)}
            onBlur={isLocked ? undefined : handleReferenceBlur}
            readOnly={isLocked}
            size="xs"
            maw={250}
          />
        )}
      </Stack>

      <Modal
        opened={showServiceForm}
        onClose={resetServiceForm}
        title={editingService ? 'Modifier le service' : 'Ajouter un service'}
        centered
      >
        <Stack gap="sm">
          <Select
            label="Service"
            placeholder="Choisir un service"
            data={filteredServices.map((s) => s.service_name)}
            value={selectedService}
            onChange={setSelectedService}
            data-autofocus
          />
          <NumberInput
            label="Prix (€)"
            value={price}
            onChange={setPrice}
            min={0}
            decimalScale={2}
            fixedDecimalScale
            readOnly={isBoutique}
          />
          <TextInput
            label="Instructions"
            placeholder="Instructions (optionnel)"
            value={instructions}
            onChange={(e) => setInstructions(e.currentTarget.value)}
          />
          <Group justify="flex-end" mt="md">
            <Button onClick={handleSubmitService} disabled={!selectedService || isMissingPrice}>
              {editingService ? 'Modifier' : 'Ajouter'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <ArticleFormModal
        opened={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSubmit={handleEditArticle}
        articleServices={articleServices}
        isCompanyCustomer={isCompanyCustomer}
        existingReferences={existingReferences}
        title="Modifier l'article"
        submitLabel="Confirmer"
      />
    </Paper>
  )
}
